import React from "react";
import useUploadFiles from "../../../hooks/useUploadFiles";
import dayjs from "dayjs";

// components

import Upload from "../../../component/modals/upload";
import TextInput from "../../../component/element/forms/TextInput";
import DateInput from "../../../component/element/forms/DateInput";
import Modal from "../../../component/ui/modal";

// state
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  IUpdateCompanyFile,
  IUploadCompanyFilesResponseData,
} from "../../../store/types/companies";
import { update_company_file } from "../../../services/apis/companies";
import { useParams } from "react-router-dom";

type IUploadDocument = {
  visible: boolean;
  onHandler: () => void;
  document?: IUploadCompanyFilesResponseData | null;
};

type DocumentFormData = IUpdateCompanyFile & {
  document_file: string;
  issuing_body: string;
  issuing_date: string;
  expiring_date: string;
  name: string;
};

type DocumentFormErrors = {
  name: string;
  issuing_body: string;
  issuing_date: string;
  expiring_date: string;
  document_file: string;
};

const EditUploadDocument: React.FC<IUploadDocument> = ({
  visible,
  onHandler,
  document,
}) => {
  const { id } = useParams();
  const companyId = Number(id);
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.companies.updatingFileId === document?.id,
  );
  const { loader, uploadFile, deleteFiles } = useUploadFiles();
  const [data, dataSet] = React.useState<DocumentFormData>({
    name: "",
    document_file: "",
    issuing_body: "",
    expiring_date: "",
    issuing_date: "",
  });
  const [error, errorSet] = React.useState<DocumentFormErrors>({
    expiring_date: "",
    issuing_body: "",
    issuing_date: "",
    name: "",
    document_file: "",
  });

  const handleOk = async () => {
    const nextErrors: DocumentFormErrors = {
      name: data.name.trim() ? "" : "error: this field is required",
      issuing_body: data.issuing_body.trim()
        ? ""
        : "error: this field is required",
      issuing_date: data.issuing_date ? "" : "error: this field is required",
      expiring_date: data.expiring_date ? "" : "error: this field is required",
      document_file: data.document_file ? "" : "error: this field is required",
    };
    errorSet(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    if (!document || !Number.isInteger(companyId) || companyId <= 0) {
      return;
    }

    const succeeded = await update_company_file(
      dispatch,
      companyId,
      document.id,
      {
        ...data,
        issuing_date: data.issuing_date.slice(0, 10),
        expiring_date: data.expiring_date.slice(0, 10),
      },
    );
    if (succeeded) onHandler();
  };

  const onChangeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    dataSet((prev) => ({ ...prev, [name]: value }));
    errorSet((prev) => ({ ...prev, [name]: "" }));
  };

  // Pre-fill form when document is provided
  React.useEffect(() => {
    if (document && visible) {
      dataSet({
        name: document.name || "",
        document_file: document.document_file || "",
        issuing_body: document.issuing_body || "",
        expiring_date: document.expiring_date
          ? dayjs(document.expiring_date).format("YYYY-MM-DD hh:mm:ss")
          : "",
        issuing_date: document.issuing_date
          ? dayjs(document.issuing_date).format("YYYY-MM-DD hh:mm:ss")
          : "",
      });
    } else if (!document && visible) {
      dataSet({
        name: "",
        document_file: "",
        issuing_body: "",
        expiring_date: "",
        issuing_date: "",
      });
    }
  }, [document, visible]);

  return (
    <Modal
      title="Edit Document"
      open={visible}
      onClose={onHandler}
      onOkClick={handleOk}
      variant="medium"
      isLoading={loading}
    >
      <div className="w-full ">
        <div className="">
          <Upload
            label="PDF"
            title="Upload opportunity File"
            name="opportunity_file"
            loading={loader}
            status={error.document_file}
            completed={Boolean(data.document_file)}
            onChange={async (fileOrFiles) => {
              const paths = await uploadFile(fileOrFiles as File, "documents");
              if (paths.length > 0) {
                const file_path = paths[0]?.base_url + "/" + paths[0]?.path;
                dataSet((prev) => ({ ...prev, document_file: file_path }));
                errorSet((prev) => ({ ...prev, document_file: "" }));
              }
            }}
            onDelete={async () => {
              if (data.document_file) {
                const res = await deleteFiles(data.document_file);
                if (res) {
                  dataSet((prev) => ({ ...prev, document_file: "" }));
                  errorSet((prev) => ({ ...prev, document_file: "" }));
                }
              }
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <TextInput
          label="Document Name"
          name="name"
          onChange={onChangeText}
          placeholder="Document Name"
          type="text"
          value={data.name}
          status={error.name}
        />

        <TextInput
          label="Issuing Body"
          name="issuing_body"
          onChange={onChangeText}
          placeholder="Issuing Body"
          type="text"
          value={data.issuing_body}
          status={error.issuing_body}
        />

        <DateInput
          label="Issuing Date"
          value={data.issuing_date}
          error={error.issuing_date}
          onChange={(date) => {
            dataSet((prev) => ({ ...prev, issuing_date: date }));
            errorSet((prev) => ({ ...prev, issuing_date: "" }));
          }}
        />

        <DateInput
          label="Expiring Date"
          value={data.expiring_date}
          error={error.expiring_date}
          onChange={(date) => {
            dataSet((prev) => ({ ...prev, expiring_date: date }));
            errorSet((prev) => ({ ...prev, expiring_date: "" }));
          }}
        />
      </div>
    </Modal>
  );
};

export default EditUploadDocument;
